import { useCallback, useMemo, useState } from 'react';

import { useCoverageAvailability } from '@apps/web/features/coverage/api/coverage_queries';
import {
    type CoverageRow,
    type DayPartKey,
    EMPTY_FILTERS,
    type WorkWeekStartDay,
} from '@apps/web/features/coverage/model/coverage_types';
import {
    type CoverageKpis,
    type CoverageSearchState,
    type CoverageSearchUpdate,
    type CoverageView,
    deriveCoverageDashboardData,
} from '@apps/web/features/coverage/model/dashboard_model';
import { type RequestStatus } from '@apps/web/features/coverage/model/request_model';
import {
    type CoverageArea,
    type CoverageBucket,
} from '@apps/web/features/coverage/model/rollup_model';
import {
    type CoverageSelectedCell,
    type CoverageSelection,
    EMPTY_SELECTION,
    rowsForSelection,
    selectionLabelFor,
    selectionSentenceFor,
} from '@apps/web/features/coverage/model/selection_model';
import { useCoverageAnalytics } from '@apps/web/features/coverage/model/use_coverage_analytics';
import { useCoverageExports } from '@apps/web/features/coverage/model/use_coverage_exports';
import {
    type CoverageMapStatus,
    useCoverageMap,
} from '@apps/web/features/coverage/model/use_coverage_map';
import { shiftWeek } from '@apps/web/features/coverage/model/week_model';
import { type WorkforceMapPoint } from '@libraries/typescript/api_client';

export type {
    CoverageSearchState,
    CoverageSearchUpdate,
    CoverageView,
} from '@apps/web/features/coverage/model/dashboard_model';
export { type CoverageMapStatus } from '@apps/web/features/coverage/model/use_coverage_map';

export type CoverageDashboard = {
    readonly isLoading: boolean;
    readonly hasError: boolean;
    readonly refreshErrorMessage: string | null;
    readonly retry: () => void;
    readonly weekStart: string | undefined;
    readonly weekEnd: string | undefined;
    readonly workWeekStartDay: WorkWeekStartDay;
    readonly rows: Array<CoverageRow>;
    readonly facetOptions: ReturnType<
        typeof deriveCoverageDashboardData
    >['facetOptions'];
    readonly kpis: CoverageKpis;
    readonly coverage: Array<CoverageBucket>;
    readonly mapStatus: CoverageMapStatus;
    readonly mapErrorMessage: string | null;
    readonly mapRefreshErrorMessage: string | null;
    readonly mapPoints: ReadonlyArray<WorkforceMapPoint>;
    readonly visibleCaregiverIds: ReadonlySet<string>;
    readonly retryMap: () => void;
    readonly hasAnyCaregivers: boolean;
    readonly hasAnyAvailabilityData: boolean;
    readonly filtersActive: boolean;
    // Changes whenever the organization or any filter/week input that
    // redefines the row set changes (excludes view/area, which don't).
    // Consumers key a remountable subtree on this to reset row-scoped local
    // state (e.g. bulk selection) instead of hand-rolling a reset effect.
    readonly rowsResetKey: string;
    readonly goToWeek: (offsetWeeks: number) => void;
    readonly goToCurrentWeek: () => void;
    readonly setFilters: (update: CoverageSearchUpdate) => void;
    readonly clearFilters: () => void;
    readonly clearEpoch: number;
    readonly setView: (view: CoverageView) => void;
    readonly setArea: (area: CoverageArea) => void;
    readonly toggleUnknownOnly: () => void;
    readonly selectCoverageCells: (
        cells: ReadonlyArray<CoverageSelectedCell>,
    ) => void;
    readonly selectCoverageCell: (
        areaKey: string,
        group: 'weekday' | 'weekend',
        dayPart: DayPartKey,
        options?: { readonly additive?: boolean },
    ) => void;
    readonly selectCoverageArea: (areaKey: string) => void;
    readonly selection: CoverageSelection;
    readonly selectionRows: ReadonlyArray<CoverageRow>;
    readonly selectionLabel: string | null;
    readonly selectionSentence: string | null;
    readonly clearSelection: () => void;
    readonly setIncludeInactive: (includeInactive: boolean) => void;
    readonly setIncludeDoNotContact: (value: boolean) => void;
    readonly exportStatus: RequestStatus;
    readonly exportError: string | null;
    readonly dismissExportError: () => void;
    readonly exportCsv: () => Promise<void>;
    readonly exportXlsx: () => Promise<void>;
};

export function useCoverageDashboard({
    organizationId,
    search,
    onSearchChange,
}: {
    readonly organizationId: string | null;
    readonly search: CoverageSearchState;
    readonly onSearchChange: (update: CoverageSearchUpdate) => void;
}): CoverageDashboard {
    const {
        data,
        error,
        hasRequestFailed,
        isLoading,
        mutate: mutateAvailability,
    } = useCoverageAvailability({
        organizationId,
        weekStart: search.week,
        includeInactive: search.includeInactive,
        includeDoNotContact: search.includeDoNotContact,
    });
    const derived = useMemo(
        () => deriveCoverageDashboardData(data, search),
        [data, search],
    );

    useCoverageAnalytics({
        organizationId,
        isReady: !isLoading,
        currentView: search.view,
    });

    const coverageMap = useCoverageMap({
        organizationId,
        enabled: search.view === 'map',
        includeInactive: search.includeInactive,
        includeDoNotContact: search.includeDoNotContact,
    });

    const navigationWeekStart = search.week ?? data?.week_start;
    const goToWeek = useCallback(
        (offsetWeeks: number) => {
            if (navigationWeekStart) {
                onSearchChange({
                    week: shiftWeek(navigationWeekStart, offsetWeeks),
                });
            }
        },
        [navigationWeekStart, onSearchChange],
    );
    const goToCurrentWeek = useCallback(() => {
        onSearchChange({ week: undefined });
    }, [onSearchChange]);
    const setFilters = useCallback(
        (update: CoverageSearchUpdate) => {
            const touchesCellSelection =
                update.days !== undefined || update.dayParts !== undefined;
            onSearchChange(
                touchesCellSelection
                    ? { ...update, matchMode: 'every' }
                    : update,
            );
        },
        [onSearchChange],
    );
    const [clearEpoch, setClearEpoch] = useState(0);
    const clearFilters = useCallback(() => {
        setClearEpoch((epoch) => epoch + 1);
        onSearchChange({ ...EMPTY_FILTERS });
    }, [onSearchChange]);
    const setView = useCallback(
        (view: CoverageView) => onSearchChange({ view }),
        [onSearchChange],
    );
    const setArea = useCallback(
        (area: CoverageArea) => {
            // Area keys are scoped to one grouping and "Unknown" exists in
            // all three, so a kept selection would aim at other caregivers.
            setSelection(EMPTY_SELECTION);
            onSearchChange({ area });
        },
        [onSearchChange],
    );
    const setIncludeDoNotContact = useCallback(
        (includeDoNotContact: boolean) =>
            onSearchChange({ includeDoNotContact }),
        [onSearchChange],
    );
    const setIncludeInactive = useCallback(
        (includeInactive: boolean) => onSearchChange({ includeInactive }),
        [onSearchChange],
    );
    const toggleUnknownOnly = useCallback(() => {
        onSearchChange({
            days: [],
            dayParts: [],
            matchMode: 'every',
            availability:
                search.availability === 'unknown_only' ? 'any' : 'unknown_only',
        });
    }, [onSearchChange, search.availability]);
    // Selection is deliberately NOT a filter: writing it into offices/days
    // collapsed the grid to the clicked row and rescored every other cell.
    const [selection, setSelection] =
        useState<CoverageSelection>(EMPTY_SELECTION);
    // Every input that redefines the row set: the cells keep their keys but
    // stop meaning the same caregivers, so a kept selection texts the wrong
    // people. Same key the row set resets on.
    const { view: _resetView, area: _resetArea, ...rowRelevantSearch } = search;
    const rowsResetKey = `${organizationId ?? 'none'}:${JSON.stringify(rowRelevantSearch)}`;
    const [selectionResetKey, setSelectionResetKey] = useState(rowsResetKey);
    if (selectionResetKey !== rowsResetKey) {
        setSelectionResetKey(rowsResetKey);
        setSelection(EMPTY_SELECTION);
    }
    // A drag hands over the whole rectangle, so the selection is replaced
    // wholesale rather than accumulated cell by cell.
    const selectCoverageCells = useCallback(
        (cells: ReadonlyArray<CoverageSelectedCell>) => {
            setSelection(
                cells.length === 0
                    ? EMPTY_SELECTION
                    : { kind: 'cells', cells: [...cells] },
            );
        },
        [],
    );
    const selectCoverageCell = useCallback(
        (
            areaKey: string,
            group: 'weekday' | 'weekend',
            dayPart: DayPartKey,
            options?: { readonly additive?: boolean },
        ) => {
            setSelection((previous) => {
                const base =
                    options?.additive === true && previous.kind === 'cells'
                        ? previous.cells
                        : [];
                // Shift-clicking the same cell twice takes it back out.
                const kept = base.filter(
                    (cell) =>
                        cell.areaKey !== areaKey ||
                        cell.group !== group ||
                        cell.dayPart !== dayPart,
                );
                const cells =
                    kept.length === base.length
                        ? [...kept, { areaKey, group, dayPart }]
                        : kept;
                return cells.length === 0
                    ? EMPTY_SELECTION
                    : { kind: 'cells', cells };
            });
        },
        [],
    );
    // An area name asks about the whole week, so no slot travels with it.
    const selectCoverageArea = useCallback((areaKey: string) => {
        setSelection({ kind: 'area', areaKeys: [areaKey] });
    }, []);
    const clearSelection = useCallback(() => setSelection(EMPTY_SELECTION), []);
    const selectionRows = useMemo(
        () => rowsForSelection(derived.coverageRows, selection, search.area),
        [derived.coverageRows, selection, search.area],
    );
    const areaLabelByKey = useMemo(
        () =>
            new Map(
                derived.coverage.map((bucket) => [
                    bucket.areaKey,
                    bucket.areaLabel,
                ]),
            ),
        [derived.coverage],
    );
    const selectionLabel = useMemo(
        () =>
            selectionLabelFor(
                selection,
                (areaKey) => areaLabelByKey.get(areaKey) ?? areaKey,
            ),
        [areaLabelByKey, selection],
    );
    const selectionSentence = useMemo(
        () =>
            selectionSentenceFor(
                selection,
                (areaKey) => areaLabelByKey.get(areaKey) ?? areaKey,
            ),
        [areaLabelByKey, selection],
    );

    const exports = useCoverageExports({
        data,
        rows: derived.rows,
        workWeekStartDay: derived.workWeekStartDay,
    });

    return {
        isLoading,
        hasError: hasRequestFailed && data === undefined,
        refreshErrorMessage:
            error !== undefined && data !== undefined
                ? 'We couldn’t refresh this view. Showing previously loaded data.'
                : null,
        retry: () => {
            void mutateAvailability();
        },
        weekStart: data?.week_start ?? search.week,
        weekEnd:
            data?.week_end ??
            (search.week ? shiftWeek(search.week, 1) : undefined),
        workWeekStartDay: derived.workWeekStartDay,
        rows: derived.rows,
        facetOptions: derived.facetOptions,
        kpis: derived.kpis,
        coverage: derived.coverage,
        mapStatus: coverageMap.status,
        mapErrorMessage: coverageMap.errorMessage,
        mapRefreshErrorMessage: coverageMap.refreshErrorMessage,
        mapPoints: coverageMap.points,
        visibleCaregiverIds: derived.visibleCaregiverIds,
        retryMap: coverageMap.retry,
        hasAnyCaregivers: derived.hasAnyCaregivers,
        hasAnyAvailabilityData: derived.hasAnyAvailabilityData,
        filtersActive: derived.filtersActive,
        rowsResetKey,
        goToWeek,
        goToCurrentWeek,
        setFilters,
        clearFilters,
        clearEpoch,
        setView,
        setArea,
        toggleUnknownOnly,
        selectCoverageCell,
        selectCoverageCells,
        selectCoverageArea,
        selection,
        selectionRows,
        selectionLabel,
        selectionSentence,
        clearSelection,
        setIncludeInactive,
        setIncludeDoNotContact,
        exportStatus: exports.status,
        exportError: exports.error,
        dismissExportError: exports.dismissError,
        exportCsv: exports.exportCsv,
        exportXlsx: exports.exportXlsx,
    };
}
